import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimelineIcon from '@mui/icons-material/Timeline';
import { usersApi, templateApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Format date
const formatDate = (timestamp) => {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Reusable User Case Info Component for modals
const UserCaseInfo = ({ user }) => {
  const hs = user?.health_summary;
  if (!hs) return null;

  return (
    <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" fontWeight={600}>
          User Case Information
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, fontSize: '0.875rem' }}>
          {/* Demographics with menstruation status */}
          <Box>
            <Typography variant="caption" color="text.secondary">Demographics</Typography>
            <Typography variant="body2">
              {hs.gender?.replace(/_/g, ' ')} • Age {hs.age || hs.age_group?.replace(/_/g, '-')}
              {hs.menstruation_status_display && ` • ${hs.menstruation_status_display}`}
            </Typography>
          </Box>

          {/* All Health Goals */}
          <Box>
            <Typography variant="caption" color="text.secondary">Health Goals</Typography>
            {hs.health_priorities_display?.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {hs.health_priorities_display.map((goal, i) => (
                  <Chip
                    key={i}
                    label={goal}
                    size="small"
                    color={i === 0 ? "primary" : "default"}
                    variant={i === 0 ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2">
                {hs.health_goal_display || hs.health_goal?.replace(/_/g, ' ') || 'Not specified'}
              </Typography>
            )}
          </Box>

          {/* Allergies */}
          {hs.allergies?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Allergies</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {hs.allergies.map((a, i) => (
                  <Chip key={i} label={a.replace(/_/g, ' ')} size="small" color="error" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Medical Conditions - use display names if available */}
          {(hs.medical_conditions_display?.length > 0 || hs.medical_conditions?.length > 0) && (
            <Box>
              <Typography variant="caption" color="text.secondary">Medical Conditions</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {(hs.medical_conditions_display || hs.medical_conditions).map((c, i) => (
                  <Chip key={i} label={typeof c === 'string' ? c.replace(/_/g, ' ') : c} size="small" color="warning" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Diet Preferences */}
          {hs.diet_preferences && (
            <Box>
              <Typography variant="caption" color="text.secondary">Diet Preferences</Typography>
              <Typography variant="body2">{hs.diet_preferences}</Typography>
            </Box>
          )}

          {/* Household */}
          {(hs.people_count || hs.meal_preference) && (
            <Box>
              <Typography variant="caption" color="text.secondary">Household</Typography>
              <Typography variant="body2">
                {hs.people_count && `${hs.people_count} people`}
                {hs.people_count && hs.meal_preference && ' • '}
                {hs.meal_preference?.replace(/_/g, ' ')}
              </Typography>
            </Box>
          )}

          {/* Additional Preferences */}
          {hs.additional_preferences && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="text.secondary">Additional Preferences</Typography>
              <Typography variant="body2">{hs.additional_preferences}</Typography>
            </Box>
          )}

          {/* Additional Notes */}
          {hs.additional_notes && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="text.secondary">Additional Notes</Typography>
              <Typography variant="body2">{hs.additional_notes}</Typography>
            </Box>
          )}

          {/* Complex Case Reasons */}
          {hs.is_complex_case && hs.complex_case_reasons?.length > 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="text.secondary">Complex Case Reasons</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {hs.complex_case_reasons.map((r, i) => (
                  <Chip key={i} label={r} size="small" color="error" />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// User Card Component (for mobile view)
const UserCard = ({ user, onNavigateCheckin, onNavigateTimeline, onCreateEvaluation }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Avatar
          sx={{
            bgcolor: user.is_complex_case ? 'error.light' : user.needs_initial_evaluation ? 'warning.light' : 'primary.light',
            color: user.is_complex_case ? 'error.main' : user.needs_initial_evaluation ? 'warning.main' : 'primary.main',
          }}
        >
          <PersonIcon />
        </Avatar>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {user.first_name}
            </Typography>
            {user.is_complex_case && (
              <Chip
                icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                label="Complex"
                size="small"
                color="error"
                variant="outlined"
              />
            )}
            {user.needs_initial_evaluation && (
              <Chip
                icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                label="Needs Evaluation"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <Typography variant="caption" color="text.secondary">
              {user.gender} • {user.age_group?.replace(/_/g, '-')}
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1.5}>
            {user.health_priorities?.slice(0, 3).map((priority, idx) => (
              <Chip
                key={idx}
                label={priority.replace(/_/g, ' ')}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            ))}
            {user.health_priorities?.length > 3 && (
              <Chip
                label={`+${user.health_priorities.length - 3}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Last check-in: {formatDate(user.last_checkin)}
              </Typography>
            </Box>

            {/* Status chips for active threads */}
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {user.has_open_evaluation && (
                <Chip
                  icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                  label="Evaluation Active"
                  size="small"
                  color="warning"
                />
              )}
              {user.has_open_checkin && (
                <Chip
                  icon={<ChatIcon sx={{ fontSize: 14 }} />}
                  label="Check-in Active"
                  size="small"
                  color="info"
                />
              )}
            </Box>
          </Box>

          {/* Action buttons */}
          <Box display="flex" gap={1} mt={1}>
            {/* Evaluate button: Show if needs_initial_evaluation AND no open evaluation */}
            {user.needs_initial_evaluation && !user.has_open_evaluation && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<AssignmentIcon />}
                onClick={() => onCreateEvaluation(user)}
              >
                Evaluate
              </Button>
            )}
            {/* Check-in button: Show if can_send_checkin (backend auto-closes previous threads) */}
            {user.can_send_checkin && (
              <Button
                size="small"
                variant={(user.needs_initial_evaluation && !user.has_open_evaluation) ? "outlined" : "contained"}
                startIcon={<EventIcon />}
                onClick={() => onNavigateCheckin(user.id)}
              >
                Check-in
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => onNavigateTimeline(user.id)}
            >
              History
            </Button>
          </Box>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

function Users() {
  const navigate = useNavigate();
  const { user: authUser, sessionKey } = useAuth();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpenThread, setFilterOpenThread] = useState(
    searchParams.get('has_open_thread') === 'false' ? 'no' : 'all'
  );

  // Create error state (used for evaluation)
  const [createError, setCreateError] = useState(null);

  // Evaluation modal state
  const [evaluationUser, setEvaluationUser] = useState(null);
  const [selectedEvalTemplate, setSelectedEvalTemplate] = useState(null);
  const [evaluationMessage, setEvaluationMessage] = useState('');
  const [sendingEvaluation, setSendingEvaluation] = useState(false);
  const [evalTemplates, setEvalTemplates] = useState([]);
  const [evalTemplatesLoading, setEvalTemplatesLoading] = useState(false);
  const [evalSignOffs, setEvalSignOffs] = useState([]);
  const [selectedEvalSignOff, setSelectedEvalSignOff] = useState('');
  const [userTargetProfile, setUserTargetProfile] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hasOpenThread = filterOpenThread === 'no' ? false : filterOpenThread === 'yes' ? true : null;
      const data = await usersApi.getUsers(hasOpenThread);
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error('Users fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterOpenThread]);

  // Reset data and refetch when authenticated user changes (account switch)
  useEffect(() => {
    setUsers([]);
    setTotalUsers(0);
    setLoading(true);
    fetchUsers();
  }, [authUser?.id, sessionKey, fetchUsers]);

  // Fetch async review templates for evaluation
  const fetchEvalTemplates = async (userId, user) => {
    setEvalTemplatesLoading(true);
    try {
      // Try user-specific templates first
      let data = null;
      let asyncTemplates = [];
      let signOffTemplates = [];
      let targetProfile = user?.target_profile || null;

      try {
        data = await usersApi.getTemplates(userId);
        console.log('User templates response:', data);
        targetProfile = data.user_profile?.target_profile || targetProfile;

        // Extract from recommended_templates format
        if (data.recommended_templates?.async_review?.length > 0) {
          asyncTemplates = data.recommended_templates.async_review;
          signOffTemplates = data.recommended_templates.sign_offs || data.recommended_templates.sign_off || [];
        }
        // Extract from flat templates array
        else if (data.templates?.length > 0) {
          asyncTemplates = data.templates.filter(t =>
            t.category === 'ASYNC_REVIEW' || t.category?.toUpperCase() === 'ASYNC_REVIEW'
          );
          signOffTemplates = data.templates.filter(t =>
            t.category === 'SIGN_OFF' || t.category?.toUpperCase() === 'SIGN_OFF'
          );
        }
      } catch (userErr) {
        console.log('User templates endpoint failed:', userErr.message);
      }

      // If no async templates found, fall back to general templates endpoint
      if (asyncTemplates.length === 0) {
        console.log('No async templates from user endpoint, fetching all templates');
        try {
          const allTemplates = await templateApi.getAll();
          console.log('All templates response:', allTemplates);
          const templateList = allTemplates.templates || allTemplates || [];

          asyncTemplates = templateList.filter(t =>
            t.category === 'ASYNC_REVIEW' || t.category?.toUpperCase() === 'ASYNC_REVIEW'
          );
          signOffTemplates = templateList.filter(t =>
            t.category === 'SIGN_OFF' || t.category?.toUpperCase() === 'SIGN_OFF'
          );
        } catch (fallbackErr) {
          console.error('Fallback templates fetch failed:', fallbackErr);
        }
      }

      console.log('Async templates found:', asyncTemplates.length, asyncTemplates);
      console.log('Sign-off templates found:', signOffTemplates.length);
      // Log first async template to see available fields
      if (asyncTemplates.length > 0) {
        console.log('First async template keys:', Object.keys(asyncTemplates[0]));
        console.log('First async template full object:', JSON.stringify(asyncTemplates[0], null, 2));
      }

      setEvalTemplates(asyncTemplates);
      setEvalSignOffs(signOffTemplates);
      setUserTargetProfile(targetProfile);

      // Auto-select template matching user's target_profile, or first one
      if (asyncTemplates.length > 0) {
        let selectedTemplate = null;
        if (targetProfile) {
          selectedTemplate = asyncTemplates.find(t =>
            t.target_profile === targetProfile ||
            t.target_profile?.toUpperCase() === targetProfile?.toUpperCase()
          );
        }
        if (!selectedTemplate) {
          selectedTemplate = asyncTemplates[0];
        }

        setSelectedEvalTemplate(selectedTemplate.id);
        // Render the selected template with user_id to replace placeholders
        try {
          const rendered = await templateApi.render(selectedTemplate.id, { userId });
          console.log('Rendered template:', rendered);
          const message = rendered.rendered_body || rendered.body || rendered.content || '';
          setEvaluationMessage(message);
        } catch (renderErr) {
          console.error('Error rendering template:', renderErr);
          console.log('Selected template object:', selectedTemplate);
          console.log('Selected template body:', selectedTemplate.body);
          // Fallback: try to get full template body, then manually replace [Name]
          try {
            const fullTemplate = await templateApi.get(selectedTemplate.id);
            console.log('Full template fallback:', fullTemplate);
            let fallbackMessage = fullTemplate.body || fullTemplate.content || selectedTemplate.body || '';
            console.log('Using fallback message from GET:', fallbackMessage?.substring(0, 100));
            if (user?.first_name) {
              fallbackMessage = fallbackMessage.replace(/\[Name\]/gi, user.first_name);
            }
            setEvaluationMessage(fallbackMessage);
          } catch (getErr) {
            console.error('Error getting full template:', getErr);
            // Last resort: use truncated body from list
            let fallbackMessage = selectedTemplate.body || selectedTemplate.content || '';
            console.log('Using last resort body from list:', fallbackMessage?.substring(0, 100));
            if (user?.first_name) {
              fallbackMessage = fallbackMessage.replace(/\[Name\]/gi, user.first_name);
            }
            if (fallbackMessage) {
              setEvaluationMessage(fallbackMessage);
            } else {
              setEvaluationMessage('[Template body not available - please type your message manually]');
            }
          }
        }
      }

      // Auto-select first sign-off if available
      if (signOffTemplates.length > 0) {
        setSelectedEvalSignOff(signOffTemplates[0].id);
      }
    } catch (err) {
      console.error('Error fetching eval templates:', err);
      setEvalTemplates([]);
      setEvalSignOffs([]);
    } finally {
      setEvalTemplatesLoading(false);
    }
  };

  // Handle evaluation template selection
  const handleEvalTemplateChange = async (templateId) => {
    setSelectedEvalTemplate(templateId);
    try {
      // Render with user_id to replace placeholders like [Name], [allergies], [health_goal]
      const rendered = await templateApi.render(templateId, { userId: evaluationUser?.id });
      // Use rendered_body from API response, with fallback to body/content
      const message = rendered.rendered_body || rendered.body || rendered.content || '';
      setEvaluationMessage(message);
    } catch (err) {
      console.error('Error rendering eval template:', err);
      // Fallback: try to get full template body
      try {
        const fullTemplate = await templateApi.get(templateId);
        let fallbackMessage = fullTemplate.body || fullTemplate.content || '';
        if (evaluationUser?.first_name) {
          fallbackMessage = fallbackMessage.replace(/\[Name\]/gi, evaluationUser.first_name);
        }
        setEvaluationMessage(fallbackMessage);
      } catch (getErr) {
        console.error('Error getting full template:', getErr);
        // Last resort: use truncated body from list
        const template = evalTemplates.find(t => t.id === templateId);
        if (template) {
          let fallbackMessage = template.body || template.content || '';
          if (evaluationUser?.first_name) {
            fallbackMessage = fallbackMessage.replace(/\[Name\]/gi, evaluationUser.first_name);
          }
          setEvaluationMessage(fallbackMessage);
        }
      }
    }
  };

  // Open evaluation modal
  const handleOpenEvaluation = (user) => {
    setEvaluationUser(user);
    setEvaluationMessage('');
    setSelectedEvalTemplate(null);
    setEvalSignOffs([]);
    setSelectedEvalSignOff('');
    setUserTargetProfile(null);
    fetchEvalTemplates(user.id, user);
  };

  // Send evaluation
  const handleSendEvaluation = async () => {
    if (!evaluationUser || !evaluationMessage.trim()) return;
    setSendingEvaluation(true);
    setCreateError(null);
    try {
      // Build the full message with sign-off appended
      let fullMessage = evaluationMessage.trim();
      if (selectedEvalSignOff) {
        try {
          const signOffRendered = await templateApi.render(parseInt(selectedEvalSignOff), { userId: evaluationUser.id });
          const signOffText = signOffRendered.rendered_body || signOffRendered.body || signOffRendered.content || '';
          if (signOffText) {
            fullMessage = fullMessage + '\n\n' + signOffText;
          }
        } catch (signOffErr) {
          console.error('Error rendering sign-off:', signOffErr);
        }
      }

      const result = await usersApi.createEvaluation(evaluationUser.id, fullMessage);
      setEvaluationUser(null);
      setEvaluationMessage('');
      setSelectedEvalTemplate(null);
      setEvalSignOffs([]);
      setSelectedEvalSignOff('');
      setUserTargetProfile(null);
      fetchUsers();
      // Navigate to the new thread if created
      if (result.thread?.reference_id) {
        navigate(`/thread/${result.thread.reference_id}`);
      }
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to send evaluation. Please try again.');
      console.error('Send evaluation error:', err);
    } finally {
      setSendingEvaluation(false);
    }
  };

  // Filter users by search query
  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    return user.first_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="secondary.dark">
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalUsers} users with nutritionist access
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            {/* Search */}
            <TextField
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Filter by open thread */}
            <ToggleButtonGroup
              value={filterOpenThread}
              exclusive
              onChange={(_, val) => val && setFilterOpenThread(val)}
              size="small"
            >
              <ToggleButton value="all">All Users</ToggleButton>
              <ToggleButton value="no">Available for Check-in</ToggleButton>
              <ToggleButton value="yes">Has Open Thread</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              No users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Try adjusting your search' : 'No users with nutritionist access'}
            </Typography>
          </CardContent>
        </Card>
      ) : isMobile ? (
        // Mobile: Card view
        <Box>
          {filteredUsers.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              onNavigateCheckin={(userId) => navigate(`/users/${userId}/create-checkin`)}
              onNavigateTimeline={(userId) => navigate(`/users/${userId}/timeline`)}
              onCreateEvaluation={handleOpenEvaluation}
            />
          ))}
        </Box>
      ) : (
        // Desktop: Table view
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Demographics</TableCell>
                  <TableCell>Health Priorities</TableCell>
                  <TableCell>Last Check-in</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: user.is_complex_case ? 'error.light' : 'primary.light',
                            color: user.is_complex_case ? 'error.main' : 'primary.main',
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.first_name}
                          </Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {user.is_complex_case && (
                              <Chip
                                label="Complex Case"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                            {user.needs_initial_evaluation && (
                              <Chip
                                label="Needs Evaluation"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.gender} • {user.age_group?.replace(/_/g, '-')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {user.health_priorities?.slice(0, 2).map((priority, idx) => (
                          <Chip
                            key={idx}
                            label={priority.replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11 }}
                          />
                        ))}
                        {user.health_priorities?.length > 2 && (
                          <Chip
                            label={`+${user.health_priorities.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.last_checkin)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {user.has_open_evaluation && (
                          <Chip
                            icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                            label="Evaluation Active"
                            size="small"
                            color="warning"
                          />
                        )}
                        {user.has_open_checkin && (
                          <Chip
                            icon={<ChatIcon sx={{ fontSize: 14 }} />}
                            label="Check-in Active"
                            size="small"
                            color="info"
                          />
                        )}
                        {!user.has_open_evaluation && !user.has_open_checkin && (
                          <Chip
                            label="Available"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        {/* Evaluate button: Show if needs_initial_evaluation AND no open evaluation */}
                        {user.needs_initial_evaluation && !user.has_open_evaluation && (
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            startIcon={<AssignmentIcon />}
                            onClick={() => handleOpenEvaluation(user)}
                          >
                            Evaluate
                          </Button>
                        )}
                        {/* Check-in button: Show if can_send_checkin (backend auto-closes previous threads) */}
                        {user.can_send_checkin && (
                          <Button
                            size="small"
                            variant={(user.needs_initial_evaluation && !user.has_open_evaluation) ? "outlined" : "contained"}
                            startIcon={<EventIcon />}
                            onClick={() => navigate(`/users/${user.id}/create-checkin`)}
                          >
                            Check-in
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<TimelineIcon />}
                          onClick={() => navigate(`/users/${user.id}/timeline`)}
                        >
                          History
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Send Evaluation Dialog */}
      <Dialog
        open={!!evaluationUser}
        onClose={() => !sendingEvaluation && setEvaluationUser(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Send Initial Evaluation for {evaluationUser?.first_name}
          <Typography variant="caption" color="warning.main" display="block">
            Personalized case review based on user profile
            {userTargetProfile && ` (${userTargetProfile.replace(/_/g, ' ')})`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          {evalTemplatesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* User Health Summary */}
              <UserCaseInfo user={evaluationUser} />

              {evalTemplates.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Select Template ({evalTemplates.length} available)
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Evaluation Template</InputLabel>
                    <Select
                      value={selectedEvalTemplate || ''}
                      onChange={(e) => handleEvalTemplateChange(e.target.value)}
                      label="Evaluation Template"
                    >
                      {evalTemplates.map((template) => (
                        <SelectMenuItem key={template.id} value={template.id}>
                          {template.name}
                          {template.target_profile && ` (${template.target_profile.replace(/_/g, ' ')})`}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedEvalTemplate && userTargetProfile && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {evalTemplates.find(t => t.id === selectedEvalTemplate)?.target_profile === userTargetProfile
                        ? '✓ Template matches user profile'
                        : 'Note: Template profile differs from user'}
                    </Typography>
                  )}
                </Box>
              )}

              {evalSignOffs.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Sign-off
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select sign-off</InputLabel>
                    <Select
                      value={selectedEvalSignOff}
                      onChange={(e) => setSelectedEvalSignOff(e.target.value)}
                      label="Select sign-off"
                    >
                      <SelectMenuItem value="">None</SelectMenuItem>
                      {evalSignOffs.map((signOff) => (
                        <SelectMenuItem key={signOff.id} value={signOff.id}>
                          {signOff.name}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                {evalTemplates.length > 0 ? 'Message Preview (editable)' : 'Write Evaluation Message'}
              </Typography>
              <TextField
                fullWidth
                value={evaluationMessage}
                onChange={(e) => setEvaluationMessage(e.target.value)}
                multiline
                rows={12}
                placeholder="Write your personalized evaluation message..."
              />
              {selectedEvalSignOff && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Sign-off will be appended: {evalSignOffs.find(s => s.id === parseInt(selectedEvalSignOff))?.name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvaluationUser(null)} disabled={sendingEvaluation}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSendEvaluation}
            disabled={sendingEvaluation || !evaluationMessage.trim()}
            startIcon={sendingEvaluation ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}
          >
            {sendingEvaluation ? 'Sending...' : 'Send Evaluation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
